"use client";

import { useMemo, useState } from "react";

function badgeClasses(label) {
  if (label === "low") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (label === "medium") return "bg-amber-50 text-amber-800 border-amber-200";
  return "bg-rose-50 text-rose-700 border-rose-200";
}

// Render text with highlighted flagged spans
function HighlightedLetter({ text, spans }) {
  const pieces = useMemo(() => {
    if (!text) return [];
    const sorted = [...(spans || [])]
      .map((s) => ({ ...s, start: Math.max(0, s.start | 0), end: Math.max(0, s.end | 0) }))
      .filter((s) => s.end > s.start && s.start < text.length)
      .sort((a, b) => a.start - b.start);

    // Merge/clip overlaps conservatively
    const merged = [];
    for (const s of sorted) {
      const last = merged[merged.length - 1];
      if (!last || s.start > last.end) merged.push({ ...s, end: Math.min(text.length, s.end) });
      else last.end = Math.max(last.end, Math.min(text.length, s.end));
    }

    const chunks = [];
    let idx = 0;
    merged.forEach((s, i) => {
      if (idx < s.start)
        chunks.push({ key: `plain-${i}-${idx}`, type: "plain", str: text.slice(idx, s.start) });
      chunks.push({
        key: `flag-${i}-${s.start}`,
        type: "flag",
        span: s,
        str: text.slice(s.start, s.end),
      });
      idx = s.end;
    });
    if (idx < text.length) chunks.push({ key: `plain-tail-${idx}`, type: "plain", str: text.slice(idx) });
    return chunks;
  }, [text, spans]);

  return (
    <div
      className="whitespace-pre-wrap text-black"
      style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: "12pt", lineHeight: 1.5 }}
    >
      {pieces.map((p) =>
        p.type === "plain" ? (
          <span key={p.key}>{p.str}</span>
        ) : (
          <mark
            key={p.key}
            className="rounded px-0.5 border"
            style={{ background: "rgba(253,230,138,0.35)", borderColor: "rgba(251,191,36,0.6)" }}
            title={`${p.span.category}: ${p.span.reason || ""}`}
          >
            {p.str}
          </mark>
        )
      )}
    </div>
  );
}

export default function LorAuditPanel({
  text,
  meta,
  onApplyRewrite, // (newText) => void
  className = "",
}) {
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");

  async function runAudit() {
    if (!text) return;
    setLoading(true);
    setError("");
    setRes(null);
    try {
      const r = await fetch("/api/lor-audit", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ text, meta }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const parts = [
          data?.error || "Audit failed",
          data?.code ? `(${data.code})` : "",
          data?.detail ? `– ${String(data.detail).slice(0, 200)}` : "",
        ]
          .filter(Boolean)
          .join(" ");
        throw new Error(parts);
      }
      setRes(data);
    } catch (e) {
      setError(e.message || "Audit failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className={[
        "text-black mt-6 rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm",
        className,
      ].join(" ")}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">AI Audit</h3>
        <button
          type="button"
          onClick={runAudit}
          disabled={loading || !text}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-white text-sm font-semibold shadow hover:brightness-110 disabled:opacity-50"
        >
          {loading ? "Analyzing…" : "Run audit"}
        </button>
      </div>

      {!text && (
        <p className="mt-2 text-sm text-slate-500">Generate or paste a letter first.</p>
      )}

      {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}

      {res && (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span
              className={[
                "rounded-full border px-2 py-0.5",
                badgeClasses(res.ai_risk?.label || "low"),
              ].join(" ")}
            >
              AI-likeness: {res.ai_risk?.label ?? "low"}{" "}
              {res.ai_risk?.score != null ? `(${Math.round(res.ai_risk.score)}%)` : ""}
            </span>
            <span
              className={[
                "rounded-full border px-2 py-0.5",
                badgeClasses(res.originality_risk?.label || "low"),
              ].join(" ")}
            >
              Originality risk: {res.originality_risk?.label ?? "low"}{" "}
              {res.originality_risk?.score != null
                ? `(${Math.round(res.originality_risk.score)}%)`
                : ""}
            </span>
          </div>

          {Array.isArray(res.ai_risk?.reasons) && res.ai_risk.reasons.length > 0 && (
            <ul className="mt-2 list-disc pl-6 text-xs text-slate-600">
              {res.ai_risk.reasons.map((r, i) => (
                <li key={`air-${i}`}>{r}</li>
              ))}
            </ul>
          )}
          {Array.isArray(res.originality_risk?.reasons) &&
            res.originality_risk.reasons.length > 0 && (
              <ul className="mt-1 list-disc pl-6 text-xs text-slate-600">
                {res.originality_risk.reasons.map((r, i) => (
                  <li key={`or-${i}`}>{r}</li>
                ))}
              </ul>
            )}

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-800 mb-1">
              Flagged phrases (highlighted below):
            </div>
            {res.flagged_spans?.length ? (
              <div className="max-h-28 overflow-auto rounded border border-amber-200 bg-amber-50/60 p-2 text-xs text-amber-900">
                {res.flagged_spans.map((s, i) => (
                  <div key={i} className="mb-1">
                    <b>{s.category}</b>: “{s.text}”
                    {s.reason ? <> — {s.reason}</> : null}
                    {s.suggestion ? (
                      <div className="text-amber-800/90">Suggestion: {s.suggestion}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded border border-emerald-200 bg-emerald-50/60 p-2 text-xs text-emerald-800">
                No obvious boilerplate detected.
              </div>
            )}
          </div>

          {/* Highlighted view */}
          <div className="mt-3 border border-slate-200 rounded-lg p-3 max-h-80 overflow-auto bg-white">
            <HighlightedLetter text={text} spans={res.flagged_spans || []} />
          </div>

          {/* Feedback + rewrite */}
          {res.overall_feedback && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              {res.overall_feedback}
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={!res.safe_rewrite}
              onClick={() => onApplyRewrite && onApplyRewrite(res.safe_rewrite)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Apply safe rewrite
            </button>
            {!res.safe_rewrite && (
              <span className="text-xs text-slate-500">
                No rewrite suggested—looks solid.
              </span>
            )}
          </div>

          <p className="mt-2 text-[11px] text-slate-500">
            This is an advisory check (style/boilerplate heuristics). It doesn’t perform a
            database/web plagiarism scan.
          </p>
        </>
      )}
    </div>
  );
}
